import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ShieldCheck, X, FileText, User, Building2 } from "lucide-react";

const BE = process.env.REACT_APP_BACKEND_URL;

export default function AdminVerifications() {
    const [pros, setPros] = useState([]);
    const [facs, setFacs] = useState([]);

    const load = async () => {
        const { data } = await api.get("/admin/verifications");
        setPros(data.data.professionals || []);
        setFacs(data.data.facilities || []);
    };
    useEffect(() => { load(); }, []);

    const act = async (entity_type, entity_id, status) => {
        try {
            await api.post("/admin/verify", { entity_type, entity_id, status });
            toast.success(status === "VERIFIED" ? "Verificato" : "Aggiornato");
            load();
        } catch { toast.error("Errore"); }
    };

    const StatusBadge = ({ s }) => {
        const map = {
            VERIFIED: "bg-emerald-50 text-emerald-700 border-emerald-200",
            UNDER_REVIEW: "bg-amber-50 text-amber-700 border-amber-200",
            REJECTED: "bg-rose-50 text-rose-700 border-rose-200",
            PENDING: "bg-gray-50 text-gray-700 border-gray-200",
        };
        return <Badge variant="outline" className={map[s] || map.PENDING}>{s || "PENDING"}</Badge>;
    };

    return (
        <div className="max-w-6xl mx-auto">
            <h1 className="font-display text-3xl font-bold tracking-tight">Coda verifiche</h1>
            <p className="text-sm text-[#5C5C5C] mt-1 mb-6">Rivedi documenti e approva profili.</p>

            <Tabs defaultValue="pros">
                <TabsList>
                    <TabsTrigger value="pros" data-testid="tab-professionals">Professionisti ({pros.length})</TabsTrigger>
                    <TabsTrigger value="facs" data-testid="tab-facilities">Strutture ({facs.length})</TabsTrigger>
                </TabsList>

                <TabsContent value="pros" className="space-y-3 mt-4">
                    {pros.length === 0 && <div className="hl-card p-10 text-center text-[#5C5C5C]">Nessun professionista.</div>}
                    {pros.map((p) => (
                        <div key={p.id} className="hl-card p-5" data-testid={`ver-pro-${p.id}`}>
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex items-start gap-3 flex-1">
                                    <div className="w-12 h-12 rounded-full bg-[#EEF3F1] grid place-items-center">
                                        {p.photo_url
                                            ? <img src={`${BE}${p.photo_url}`} className="w-full h-full rounded-full object-cover" alt="" />
                                            : <User className="w-5 h-5 text-[#1C3F39]" />}
                                    </div>
                                    <div>
                                        <div className="font-display font-bold">{p.full_name}</div>
                                        <div className="text-sm text-[#5C5C5C]">{p.profession} · {p.city || "—"}</div>
                                        {p.albo_number && <div className="text-xs text-[#5C5C5C] mt-1">Albo: <span className="font-mono">{p.albo_number}</span></div>}
                                        {p.vat_number && <div className="text-xs text-[#5C5C5C]">P.IVA: <span className="font-mono">{p.vat_number}</span></div>}
                                        {(p.documents || []).length > 0 && (
                                            <div className="mt-3 space-y-1">
                                                {p.documents.map((d) => (
                                                    <a key={d.id} href={`${BE}${d.url}`} target="_blank" rel="noreferrer"
                                                        className="flex items-center gap-2 text-xs text-[#1C3F39] hover:underline"
                                                        data-testid={`doc-${d.id}`}>
                                                        <FileText className="w-3 h-3" />{d.filename}
                                                    </a>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                    <StatusBadge s={p.verification_status} />
                                    {p.verification_status !== "VERIFIED" && (
                                        <Button size="sm" className="bg-[#1C3F39] hover:bg-[#2A5C53]"
                                            onClick={() => act("professional", p.id, "VERIFIED")}
                                            data-testid={`verify-pro-${p.id}`}>
                                            <ShieldCheck className="w-4 h-4 mr-1" />Verifica
                                        </Button>
                                    )}
                                    {p.verification_status !== "REJECTED" && (
                                        <Button size="sm" variant="outline"
                                            onClick={() => act("professional", p.id, "REJECTED")}
                                            data-testid={`reject-pro-${p.id}`}>
                                            <X className="w-4 h-4 mr-1" />Rifiuta
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </TabsContent>

                <TabsContent value="facs" className="space-y-3 mt-4">
                    {facs.length === 0 && <div className="hl-card p-10 text-center text-[#5C5C5C]">Nessuna struttura.</div>}
                    {facs.map((f) => (
                        <div key={f.id} className="hl-card p-5" data-testid={`ver-fac-${f.id}`}>
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex items-start gap-3 flex-1">
                                    <div className="w-12 h-12 rounded bg-[#EEF3F1] grid place-items-center">
                                        {f.logo_url
                                            ? <img src={`${BE}${f.logo_url}`} className="w-full h-full rounded object-cover" alt="" />
                                            : <Building2 className="w-5 h-5 text-[#1C3F39]" />}
                                    </div>
                                    <div>
                                        <div className="font-display font-bold">{f.name}</div>
                                        <div className="text-sm text-[#5C5C5C]">{f.facility_type || "—"}</div>
                                        {f.vat_number && <div className="text-xs text-[#5C5C5C] mt-1">P.IVA: <span className="font-mono">{f.vat_number}</span></div>}
                                        {f.website && <a href={f.website} target="_blank" rel="noreferrer" className="text-xs text-[#1C3F39] hover:underline">{f.website}</a>}
                                    </div>
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                    <StatusBadge s={f.verification_status} />
                                    {f.verification_status !== "VERIFIED" && (
                                        <Button size="sm" className="bg-[#1C3F39] hover:bg-[#2A5C53]"
                                            onClick={() => act("facility", f.id, "VERIFIED")}
                                            data-testid={`verify-fac-${f.id}`}>
                                            <ShieldCheck className="w-4 h-4 mr-1" />Verifica
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </TabsContent>
            </Tabs>
        </div>
    );
}
